import React, { useCallback, useEffect, useState } from 'react';

// Closed weekly schedule grid primitive — 8 day rows × 24 hour rows ×
// 4 quarter-hour slots per cell. Pure paint + drag interaction; no
// labels, no inputs, no overlays.
//
// API:
//   value: Record<Day, boolean[96]>     // optional; falls back to all-false
//   onChange: (next) => void            // omitted when readonly
//   readonly: boolean                   // display-only; no drag, no hover
//
// Drag-to-paint with auto-fill-mode: mousedown on an empty cell paints
// `true` over the drag range; on a filled cell paints `false`. No
// external picker — the gesture decides direction (Google Calendar
// style).

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday', 'holiday'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const QUARTERS = [0, 15, 30, 45];
const SLOTS_PER_DAY = HOURS.length * 4;

// Schedule grid uses ultra-small typography that lives outside the
// public Text variant set (which dropped overline-micro and micro-8
// as too niche). The grid genuinely needs these sizes — bake the
// recipes locally.
const OVERLINE_MICRO_CLASSNAME = 'tw-text-micro tw-font-semibold tw-uppercase tw-tracking-wide tw-leading-4';
const MICRO_8_CLASSNAME = 'tw-text-micro-8 tw-leading-3 tw-text-muted-foreground';

const buildEmptySchedule = () => DAYS.reduce((acc, day) => {
    acc[day] = Array(SLOTS_PER_DAY).fill(false);

    return acc;
}, {});

const resolveSchedule = value => {
    if (!value || Object.keys(value).length === 0) {
        return buildEmptySchedule();
    }

    return value;
};

export function Schedule({
    className: _className,
    onChange,
    readonly = false,
    style: _style,
    value
}) {
    const schedule = resolveSchedule(value);
    const isEditable = !readonly && typeof onChange === 'function';

    const [dragStart, setDragStart] = useState(null);
    const [dragEnd, setDragEnd] = useState(null);
    const [dragPaintingTo, setDragPaintingTo] = useState(null);

    const isCellSelected = (dayIdx, slotIdx) => {
        if (!dragStart || !dragEnd) {
            return false;
        }

        const minDay = Math.min(dragStart.dayIdx, dragEnd.dayIdx);
        const maxDay = Math.max(dragStart.dayIdx, dragEnd.dayIdx);
        const minSlot = Math.min(dragStart.slotIdx, dragEnd.slotIdx);
        const maxSlot = Math.max(dragStart.slotIdx, dragEnd.slotIdx);

        return dayIdx >= minDay && dayIdx <= maxDay && slotIdx >= minSlot && slotIdx <= maxSlot;
    };

    const handleMouseDown = (dayIdx, slotIdx) => {
        if (!isEditable) {
            return;
        }
        const currentValue = Boolean(schedule[DAYS[dayIdx]]?.[slotIdx]);
        const paintingTo = !currentValue;

        setDragStart({ dayIdx, slotIdx });
        setDragEnd({ dayIdx, slotIdx });
        setDragPaintingTo(paintingTo);
    };

    const handleMouseEnter = (dayIdx, slotIdx) => {
        if (dragStart && isEditable) {
            setDragEnd({ dayIdx, slotIdx });
        }
    };

    const handleMouseUp = useCallback(() => {
        if (dragStart && dragEnd && dragPaintingTo !== null && isEditable) {
            const next = { ...schedule };
            const minDay = Math.min(dragStart.dayIdx, dragEnd.dayIdx);
            const maxDay = Math.max(dragStart.dayIdx, dragEnd.dayIdx);
            const minSlot = Math.min(dragStart.slotIdx, dragEnd.slotIdx);
            const maxSlot = Math.max(dragStart.slotIdx, dragEnd.slotIdx);

            for (let d = minDay; d <= maxDay; d += 1) {
                const dayName = DAYS[d];
                const dayArray = [...(next[dayName] || Array(SLOTS_PER_DAY).fill(false))];

                for (let s = minSlot; s <= maxSlot; s += 1) {
                    dayArray[s] = dragPaintingTo;
                }
                next[dayName] = dayArray;
            }

            onChange(next);
        }
        setDragStart(null);
        setDragEnd(null);
        setDragPaintingTo(null);
    }, [dragStart, dragEnd, dragPaintingTo, isEditable, schedule, onChange]);

    useEffect(() => {
        if (!isEditable) {
            return undefined;
        }
        window.addEventListener('mouseup', handleMouseUp);

        return () => window.removeEventListener('mouseup', handleMouseUp);
    }, [handleMouseUp, isEditable]);

    return (
        <div className="tw-flex tw-flex-col tw-w-full tw-bg-divider tw-text-foreground tw-gap-px tw-border tw-border-solid tw-border-divider">
            {/* Column header: HR + day labels.
              *
              * Same gap-px + bg-divider strategy used in the main
              * grid — eliminates doubled borders where adjacent
              * boxes share an edge. Inner cells paint `bg-surface`
              * so the parent's `bg-divider` only shows in the 1px
              * gaps. */}
            <div className="tw-flex tw-flex-none tw-gap-px tw-bg-divider">
                <div className="tw-w-12 tw-flex-shrink-0 tw-p-1 tw-flex tw-items-center tw-justify-center tw-bg-surface">
                    <span className={OVERLINE_MICRO_CLASSNAME}>HR</span>
                </div>
                <div className="tw-flex tw-flex-grow tw-gap-px tw-bg-divider">
                    {DAYS.map(day => (
                        <div
                            key={day}
                            className="tw-flex-1 tw-py-1 tw-overflow-hidden tw-bg-surface"
                        >
                            <div className="tw-flex tw-justify-center">
                                <span className={OVERLINE_MICRO_CLASSNAME}>{i18next.t(`weekdays.${day}`)}</span>
                            </div>
                            <div className="tw-flex tw-mt-0.5">
                                {QUARTERS.map(q => (
                                    <div key={q} className="tw-flex tw-flex-1 tw-justify-center">
                                        <span className={MICRO_8_CLASSNAME}>{q}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="tw-flex tw-gap-px tw-bg-divider">
                {/* Vertical hour labels */}
                <div className="tw-w-12 tw-flex-shrink-0 tw-flex tw-flex-col tw-gap-px tw-bg-divider">
                    {HOURS.map(hour => (
                        <div
                            key={hour}
                            className="tw-flex tw-items-center tw-justify-center tw-flex-1 tw-bg-surface"
                        >
                            <span className={MICRO_8_CLASSNAME}>{`${String(hour).padStart(2, '0')}:00`}</span>
                        </div>
                    ))}
                </div>

                {/* Main grid.
                  *
                  * Per-cell `border-r/border-b` lines were painted at
                  * `border-border/20` — a pale gray that read as a
                  * visible white-ish stripe wherever a filled cell sat
                  * next to it (filled cells use `bg-primary` deep teal,
                  * so the faint border lightened against it). Sub-pixel
                  * rounding on the `aspect-square` cells made it worse
                  * by snapping the borders inconsistently between rows.
                  *
                  * Replaced with `gap-px` + a `bg-divider` parent.
                  * The 1px gap reveals the parent's tinted background
                  * between cells. Filled cells paint up to their own
                  * edge, so the gap stays a clean line regardless of
                  * fill state and there are no sub-pixel artefacts.
                  * `--divider` is the canonical token for dense
                  * grid subdivisions — lighter than `border`-class
                  * surfaces, darker than `muted`. Used here for every
                  * line in the schedule (outer frame, day-column
                  * borders, hour-label borders, cell gaps). */}
                <div className="tw-flex tw-flex-grow tw-gap-px tw-bg-divider">
                    {DAYS.map((day, dayIndex) => (
                        <div
                            key={day}
                            className="tw-flex-1 tw-grid tw-grid-cols-4 tw-gap-px tw-bg-divider"
                            style={{
                                aspectRatio: '4 / 24',
                                gridTemplateRows: 'repeat(24, 1fr)'
                            }}
                        >
                            {HOURS.flatMap(hour => QUARTERS.map((quarter, qIndex) => {
                                const slotIndex = (hour * 4) + qIndex;
                                const isOn = Boolean(schedule[day]?.[slotIndex]);
                                const isInDrag = isCellSelected(dayIndex, slotIndex);
                                const previewOn = isInDrag && dragPaintingTo !== null ? dragPaintingTo : isOn;
                                const cellClassName = [
                                    'tw-transition-colors',
                                    previewOn ? 'tw-bg-primary' : 'tw-bg-background',
                                    isEditable && !isInDrag ? 'hover:tw-bg-accent' : '',
                                    isEditable ? 'tw-cursor-pointer' : ''
                                ].filter(Boolean).join(' ');

                                // Filled cells paint a 1px outset shadow in
                                // `--divider-strong` so the 1px parent gap
                                // around them shows as a darker line. Empty
                                // cells render no shadow, so empty/empty
                                // gaps keep the lighter `--divider` line
                                // bleeding through from the parent.
                                const cellStyle = previewOn
                                    ? { boxShadow: '0 0 0 1px hsl(var(--divider-strong))' }
                                    : undefined;

                                return (
                                    <div
                                        key={`${hour}-${quarter}`}
                                        className={cellClassName}
                                        style={cellStyle}
                                        onMouseDown={isEditable ? () => handleMouseDown(dayIndex, slotIndex) : undefined}
                                        onMouseEnter={isEditable ? () => handleMouseEnter(dayIndex, slotIndex) : undefined}
                                    />
                                );
                            }))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
